/**
 * Default layout theme tokens — the report-grade "chrome" shared by every chart
 * (typography, surfaces, gridlines, legend, title). Centralized here, exactly
 * like DEFAULT_PALETTE in ./palette, so the look is tuned in one place and can
 * be themed later instead of being hardcoded across makeLayout.
 */

/**
 * Web-safe sans stack (matplotlib-ish look without shipping a custom font). The
 * matplotlib default (DejaVu Sans) isn't installed in browsers, so we lead with
 * Verdana/Segoe UI — widely available and close in proportions.
 */
// Inter is loaded app-wide via @micronet/ui-kit's styles (imported by the kit's
// index.css and by every consumer, e.g. conciliatron), so naming it here is
// enough — no per-package font bundling. system-ui leads the fallback for the rare
// document that doesn't load it. One family for text and numbers reads as technical
// report typography.
const FONT_FAMILY = "'Inter', system-ui, Helvetica, Arial, sans-serif";

/**
 * Base font for ticks and the legend — the chart "chrome". Slightly smaller and a
 * muted slate (not black) so the data, not the scaffolding, carries the eye.
 */
export const CHART_FONT = {
  family: FONT_FAMILY,
  size: 12.5,
  color: '#5B6573',
} as const;

/**
 * Hover label font — the same size as the rest of the chrome, and scaled with the
 * figure like the ticks are.
 *
 * Left to itself the hover label is the one piece of type that does NOT take part
 * in the scale: Plotly's default is a flat 13px (`HOVERFONTSIZE`, fx/constants),
 * and nothing authored here reaches it. So it is wrong in BOTH directions, and
 * only right at TYPO_REFERENCE_WIDTH where the chrome also lands on 12.5:
 *
 *   figure 428px (a card, scale clamped to 0.75) → chrome 9.4, hover 13  (+38%)
 *   figure 650px (the reference)                 → chrome 12.5, hover 13 ( ~0%)
 *   figure 1000px (the export canvas)            → chrome 19.2, hover 13 (−32%)
 *
 * The card case is what gives it away on screen: the tooltip reads as the largest
 * type in a figure whose every other label was deliberately shrunk.
 *
 * Deliberately carries no `color`: Plotly picks black or white per hover box to
 * contrast with the trace's own background, and pinning one here would break that
 * on half the palette.
 *
 * And the size is DERIVED from CHART_FONT rather than restated — the opposite of
 * AXIS_TITLE_FONT below, whose whole point is to stay put when CHART_FONT moves.
 * Here "the hover reads like the ticks" IS the requirement, so the two must never
 * drift apart; writing the number twice is how they would.
 */
export const HOVER_FONT = {
  family: FONT_FAMILY,
  size: CHART_FONT.size,
} as const;

/**
 * Chart title font (rendered inside the figure, so it travels in PNG exports).
 * SemiBold (600) and near-black — present without being aggressive.
 */
export const TITLE_FONT = {
  family: FONT_FAMILY,
  size: 17,
  color: '#1F2733',
  weight: 600,
} as const;

/**
 * Top padding (px, at the reference figure width) between the figure's top edge
 * and the title.
 *
 * Sized ONLY to stop the glyphs being clipped: `title.automargin` anchors the
 * title to the *container* top (`yref: 'container'`, `yanchor: 'top'`) instead of
 * the plot area, and Plotly's default `title.pad` is 0 — so it emits
 * `<text y="0">` and the ascenders land above the SVG viewport. Measured overshoot
 * at TITLE_FONT.size 17: 4.1px off every capital/ascender. 8 covers it with ~4px to
 * spare — deliberately more than the measurement, because a PNG export can
 * rasterize with fallback font metrics if the remote webfont is unavailable there,
 * and those ascenders are not the ones this was measured against.
 *
 * It used to be 24, to clear the hover modebar's 25px band (Plotly pins that bar
 * to the graph div's top-right and offers no way to move it, so it covered the
 * last 91px of a centered title). That is no longer bought: the bar is DOM chrome
 * that appears on hover, is translucent, and is excluded from PNG exports — so 24
 * spent permanent vertical space in every figure and every view to avoid a
 * transient overlap, and it read as a title floating too low in the card. The
 * overlap is accepted instead, and only bites while the pointer is inside a figure
 * whose centered title is long enough to reach the bar's 192px band.
 *
 * `automargin` absorbs the padding into the top margin, so nothing is clipped;
 * the cost is plot height (see the note in MARGIN).
 */
export const TITLE_PAD_TOP = 8;

/**
 * Horizontal breathing room (px per side) kept when wrapping the title/subtitle
 * to the figure width, so a wrapped line stops short of the border instead of
 * touching it.
 */
export const TITLE_SIDE_PAD = 12;

/**
 * Line height as a multiple of the font size, used to budget vertical space for a
 * title that wrapping split into several lines.
 *
 * Plotly anchors the title block by its LAST line, so extra lines grow UPWARD.
 * Growing the pad by one of these per extra line compensates only APPROXIMATELY —
 * measured, a 1-line title starts at y=25 and a 2-line title at y=-0.1, so the
 * first line still rises on wrap. Plotly's own advance also depends on whether the
 * title's automargin push engages, which depends on `MARGIN.base.t` and is
 * therefore per chart type. Do not read this as "the first line stays put".
 *
 * 1.3 is Plotly's LINE_SPACING (`src/constants/alignment.js`), the same constant
 * STATS_LINE_HEIGHT uses — budgeting less than Plotly advances is what made the
 * drift worse.
 */
export const TITLE_LINE_HEIGHT = 1.3;

/**
 * Ceiling on the title block, as a fraction of the figure height.
 *
 * Without it the pad grows unbounded with the line count: measured on the accuracy
 * map's title, a 60px-wide figure wrapped to 9 lines and asked for 184px of pad —
 * 47% of a 388px figure — so `automargin` silently crushed the plot to a strip.
 * Trading a visible clipped title for an invisible collapsed plot is a worse deal,
 * so past this the title is left to clip and the fix is a shorter title.
 */
export const TITLE_MAX_HEIGHT_FRACTION = 0.3;

/**
 * Padding (px) subtracted from a stats box's slot before wrapping its text, to
 * cover the annotation's own `borderpad` plus the gap to the next column.
 */
export const STATS_BOX_PAD = 16;

/**
 * Fraction of the PAPER box width (the plot area inside the margins — see the
 * `paper` derivation in ChartRenderer) that a stats box drawn INSIDE the plot may
 * use before its text wraps. Half leaves the other half readable underneath.
 */
export const STATS_BOX_INSIDE_FRACTION = 0.5;

/**
 * Floor for the stats box font when its widest token still doesn't fit its slot.
 * Entries like `mediana=0.295` hold no space, so wrapping cannot narrow them and
 * the only remaining lever is size — but not past the point of being unreadable
 * in a printed report.
 */
export const STATS_FONT_MIN_SIZE = 9;

/**
 * Fraction of the PAPER box height (the plot area inside the margins) a stats
 * column may occupy before its font steps down.
 *
 * Nearly all of it. The budget exists to keep the column INSIDE the paper — an
 * unbounded one grew to 272px on a 420px tile and spilled past the figure — not to
 * reserve room for anything else down there. 0.55 did reserve room, for a legend
 * that no longer sits under this column: with `statsBoxRight` the legend is
 * centred on the shrunken plot domain and drawn below the paper entirely
 * (`yref: 'container'`), so the gutter's lower half was being held empty against a
 * collision that cannot happen. Measured on a 648x388 card, that left 124px of
 * 276px unused while the font stepped down to fit 18 rows into the other 152px.
 *
 * The remaining 0.05 is the bottom breathing room, so the last row does not sit on
 * the x-axis line.
 */
export const STATS_BOX_MAX_HEIGHT_FRACTION = 0.95;

/**
 * Line height multiple used to estimate a stats box's height from its line count.
 * Matches Plotly's own LINE_SPACING (1.3em) for wrapped annotation text — the
 * earlier 1.25 under-estimated every line, so the height fit stopped one step too
 * early and accepted a box taller than STATS_BOX_MAX_HEIGHT_FRACTION allows.
 */
export const STATS_LINE_HEIGHT = 1.3;

/**
 * Padding (px) a stats annotation draws between its border box and its text.
 * Plotly's `borderpad`; named here because STATS_BOX_CHROME is derived from it.
 */
export const STATS_BOX_BORDERPAD = 6;

/**
 * Chrome (px) a stats annotation draws around its text. Plotly computes
 * `outerHeight = annHeight + 2 * (borderwidth + borderpad)` (annotations/draw.js),
 * so with the renderer's `borderwidth: 0, borderpad: 6` it is 2 * 6 = 12 — the
 * padding counts on BOTH edges. Counted against the height budget because the line
 * stack alone does not account for it. Derived, not a literal: the box is drawn
 * borderless (see STATS_BOX_BORDER_WIDTH), and an independent constant is exactly
 * how the earlier 13 came to disagree with the geometry it was meant to describe.
 */
export const STATS_BOX_BORDER_WIDTH = 0;
export const STATS_BOX_CHROME = 2 * (STATS_BOX_BORDER_WIDTH + STATS_BOX_BORDERPAD);

/**
 * Geometry of the right-hand stats gutter, in Plotly `paper` fractions. One home
 * for numbers that must agree across two files: `plotDomainEnd` is where the plot
 * is cut (`xaxis.domain`), and the box/legend live to the right of it. They were
 * four separate literals in makeLayout and ChartRenderer, so widening the plot
 * silently drew the box over the data.
 */
const STATS_GUTTER_GAP = 0.01;
export const STATS_GUTTER = {
  plotDomainEnd: 0.735,
  // The gutter starts one small gap after the plot ends, not an arbitrary step
  // later: every fraction between the two is column width the labels do not get,
  // and at 0.735 the column is already narrow enough that a label like
  // `cut_cp-LP max` forces the font down. Derived from `plotDomainEnd` so widening
  // the plot can never leave the block stranded — or overlap it.
  columnsStart: 0.735 + STATS_GUTTER_GAP,
  singleStart: 0.735 + STATS_GUTTER_GAP,
  end: 0.99,
} as const;

/**
 * Where the VALUE column is right-aligned inside the stats gutter.
 *
 * The gutter is two columns, not one run of text: labels flush left from
 * `singleStart`, values flush right at this edge. Plotly cannot align a table, so
 * the two columns are two annotations sharing one line stack — see
 * `statsAnnotations`. Right-aligning the values is what makes a column of numbers
 * readable as a column instead of as ragged prose.
 */
export const STATS_VALUE_RIGHT = 0.99;

/**
 * Blank lines above a group heading.
 *
 * Zero: the headings are bold and set in caps, which separates them on their own —
 * and every blank line is a row of height the column does not have. With three
 * groups the gaps cost two rows, which on a 388px card is the difference between
 * the block clearing the legend and printing through it.
 */
export const STATS_GROUP_GAP_LINES = 0;

/**
 * Minimum gutter (px, at the reference figure width) between the label column and
 * the value column, so the longest label never touches its own value.
 */
export const STATS_COLUMN_GAP = 12;

/**
 * Headroom above the data max when a stats box is drawn INSIDE the plot. That box
 * is opaque and top-anchored, so it needs the pre-legend-move 20%: the reduced
 * Y_HEADROOM is justified by the legend leaving the plot, which does not apply here.
 */
export const Y_HEADROOM_INSIDE_BOX = 0.2;

/** Subtitle font (second line under the title) — subordinate slate. */
export const SUBTITLE_FONT = {
  family: FONT_FAMILY,
  size: 12.5,
  color: '#6B7785',
} as const;

/**
 * Axis title font — larger and bold so the measured quantity reads clearly.
 * Defined explicitly (not spread from CHART_FONT) so its size/color stay put
 * when CHART_FONT changes.
 */
export const AXIS_TITLE_FONT = {
  family: FONT_FAMILY,
  size: 14,
  color: '#46505C',
  weight: 700,
} as const;

/**
 * Gap (px) between the axis tick labels and the axis title, so the title never
 * sits on top of the values. Paired with `automargin` on each axis (see
 * makeLayout) so the reserved space grows instead of the title being clipped.
 */
export const AXIS_TITLE_STANDOFF = 12;

/** Paper + plot background. */
export const SURFACE_COLOR = '#FFFFFF';

/** Soft gridline color (horizontal grid only). */
export const GRID_COLOR = '#EDF0F3';

/** Gridline width, in px. */
export const GRID_WIDTH = 1;

/**
 * Axis spine (the X/Y lines) + tick mark color — a light slate, not black, to
 * match the de-emphasized chrome. Kept darker than the grid (and not as pale as
 * a near-white) so the frame and ticks still read in print.
 */
export const AXIS_LINE_COLOR = '#AAB3BD';

/** Axis spine width, in px. */
export const AXIS_LINE_WIDTH = 1;

/** Outside tick mark length and width, in px (matplotlib-style visible ticks). */
export const TICK_LEN = 5;
export const TICK_WIDTH = 1;

/** Fill opacity of the upper/lower limit band rectangle. */
export const LIMIT_BAND_OPACITY = 0.06;

/**
 * Boxed legend **below** the plot: horizontal, centered, under the X-axis title.
 * Horizontal so a multi-series legend spreads sideways instead of stacking down.
 *
 * It used to sit *inside* the plot, top-right. Measured on a 4-series dual-axis
 * card, that box was 361x50px inside a 384x268px plot area — it covered 19% of
 * the plot height, which is precisely what Y_HEADROOM was compensating for. With
 * the legend out of the plot the data is never occluded, so the headroom drops to
 * plain breathing room (see Y_HEADROOM).
 *
 * Below and not above: at `y: 1` (the plot's top edge) Plotly does NOT treat the
 * legend as outside, so `autoexpand` reserves nothing and the box lands on top of
 * the title/subtitle — measured.
 *
 * Anchored to the figure bottom via `yref: 'container'`, not to the plot with a
 * negative `y`. A plot-relative offset is a fraction of the plot HEIGHT, so the
 * clearance over the X-axis title shrinks with the card: at `y: -0.2` it already
 * grazed that title by 4px on a 388px figure and would collide outright on a
 * shorter one. Pinned to the container the gap is constant, and `autoexpand`
 * grows the bottom margin to fit however many rows the entries need — so no
 * bottom reserve is hardcoded in MARGIN.
 *
 * Two variants pin their own `y`/`yanchor` in makeLayout instead: `legendOutside`,
 * and `statsBoxRight` TOGETHER WITH `statsBoxColumns`. Plain `statsBoxRight` falls
 * through to this style — its single stacked box needs the gutter's full height —
 * but with its `x` recentred on the shrunken plot domain.
 */
export const LEGEND_STYLE = {
  bgcolor: 'rgba(255,255,255,0.88)',
  bordercolor: GRID_COLOR,
  // Borderless: the box outline framed the entries as a separate panel competing
  // with the plot's own frame. The translucent background stays — it is what keeps
  // the entries legible where the legend shares the gutter with data.
  borderwidth: 0,
  orientation: 'h',
  x: 0.5,
  xanchor: 'center',
  y: 0,
  yanchor: 'bottom',
  yref: 'container',
} as const;

/**
 * Gap (px) between the legend and the figure's bottom edge.
 *
 * `LEGEND_STYLE` anchors the legend's bottom to the container's bottom (`y: 0`,
 * `yref: 'container'`), which puts its box flush on the viewport edge — the same
 * class of clipping TITLE_PAD_TOP fixes at the top. Measured on the scatter's PNG
 * export, the bottom row of entries lost its descenders and the box outline was
 * cut in half. Converted to a container fraction against the measured height, so
 * the gap is a constant number of pixels rather than a share of the figure.
 */
export const LEGEND_PAD_BOTTOM = 8;

/**
 * Plot margins. The title/subtitle live in the figure with `automargin`, so the
 * top only needs a small base; `base.b` fits the X axis, and the legend below it
 * needs no extra reserve here because Plotly's `autoexpand` grows the bottom to
 * fit however many rows its entries take (verified: same geometry with and
 * without a hardcoded reserve). `r` widens for a secondary (right) Y axis, or to
 * `legendGutter` for a right-hand legend.
 */
export const MARGIN = { base: { t: 62, r: 20, l: 60, b: 50 }, rightAxis: 60, legendGutter: 160 } as const;

/**
 * Fraction of headroom added above the data max. While the legend sat inside the
 * plot this had to clear the legend box (0.2 = 20%); with the legend moved to the
 * header block it is only breathing room, so the topmost point isn't welded to
 * the plot's top border.
 */
export const Y_HEADROOM = 0.05;

/**
 * Fraction of the axis SPAN (dataMax - dataMin) added as headroom below the data
 * min on the SECONDARY (right) Y axis, when `options.y2FitData` bounds it to the
 * data instead of forcing a 0 floor. Kept distinct from `Y_HEADROOM` — the right
 * axis usually carries a proportionally smaller-magnitude measure (e.g. a grade
 * in %), where the same fraction the left axis uses above its max reads as too
 * much empty space below the curve.
 */
export const Y2_FIT_DATA_BOTTOM_HEADROOM = 0.025;

/**
 * Symmetric pad, as a fraction of each axis' own span, added around a plan view's
 * data extent (`equalAspect`). Unlike Y_HEADROOM this applies to BOTH ends of BOTH
 * axes: the map's markers are block cells centred on their coordinate, so without a
 * pad the perimeter cells are drawn half outside the frame and read as clipped.
 *
 * Small on purpose — the extent is the deposit, and the aspect constraint widens
 * whichever axis it must anyway.
 */
export const EQUAL_ASPECT_PAD = 0.02;

/**
 * Right gutter of a `legendOutside` legend, as a fraction of the CONTAINER.
 *
 * The fraction — rather than letting Plotly size the gutter — is what makes the plot
 * area independent of the legend's TEXT. Anchored with `xref: 'paper'`, a legend is
 * registered through `Plots.autoMargin` with `r` = its own measured width, and that
 * width is the bounding box of the longest label. The plan view's entries carry a
 * per-level count ("Q1 … — 65" on one bench, "— 6" on another), so the right margin
 * moved by a few px per digit, and with the aspect constraint centring what it
 * shrinks, the map slid sideways between benches. Anchored with `xref: 'container'`
 * the legend instead reserves `width * (1 - x)`: a function of the figure width
 * alone. See US 2097.
 *
 * `MARGIN.legendGutter` stays the target width in px; this only expresses it in the
 * unit Plotly accepts without measuring text.
 *
 * The trade-off: nothing clamps a legend wider than its gutter any more, so an
 * over-long entry runs off the figure's right edge and is clipped by the viewport.
 * Same deal the title already takes (see TITLE_MAX_HEIGHT_FRACTION) — a clipped
 * label beats a plot area that moves under the reader.
 */
export const LEGEND_GUTTER = {
  /** Cap, so a narrow card doesn't hand half its width to the legend. */
  maxFraction: 0.4,
  /** Used until the figure width is measured — same "don't guess" rule as legendY. */
  fallbackFraction: 0.22,
  /**
   * Px Plotly draws per entry besides the label itself: the colour swatch, the gap
   * to the text and the box's own padding. Added to the measured label width so the
   * gutter fits what will actually be drawn.
   */
  entryChrome: 46,
} as const;

/**
 * Default marker diameter (px, at the reference figure width) for point marks —
 * scatter and the spatial/plan view. Overridable per widget via
 * `options.markerSize`; either way the resolved value is scaled with the figure,
 * so the dots keep the same visual weight in a card and in a report figure.
 */
export const DEFAULT_MARKER_SIZE = 8;

/**
 * Default stroke width (px, at the reference figure width) for series lines and
 * the scatter's OLS trend. Scaled with the figure for the same reason as
 * DEFAULT_MARKER_SIZE.
 */
export const SERIES_LINE_WIDTH = 2;

/**
 * Global fallback aspect ratio (width / height) for chart cards that derive
 * their height from their width. ~1.4 leans slightly wide, matplotlib-style.
 * A chart type may override it via `defaultAspectRatio`; the consumer may
 * override both via `options.aspectRatio`. See ChartCard.
 */
export const DEFAULT_ASPECT_RATIO = 1.4;

/**
 * Tick labels as plain grouped numbers — international style ("1,271"), never SI
 * ("551.4k") or scientific. For es-CL ("1.271") Plotly would also need a
 * decimal-',' / thousands-'.' setup via `layout.separators` (`',.'`); that is
 * not currently wired.
 *
 * Plotly's own formatting rather than a d3 `tickformat: ','`: Plotly places ticks
 * by repeated addition, so on a 0.1-step axis crossing zero the "0" tick is really
 * -4.44e-17, and `','` printed it at 12 significant digits ("-4.4408920985e-17").
 * The native formatter rounds every label to the step's precision.
 */
export const NUMBER_TICKS = { separatethousands: true, exponentformat: 'none' } as const;
